<template>
  <section
    v-if="publicUrl"
    class="customer-tasks-link mb-4 rounded-lg border border-border bg-card"
  >
    <div class="tasks-link-content">
      <h2 class="flex items-center gap-2 text-sm font-semibold">
        <ListTodo class="size-4 text-muted-foreground" aria-hidden="true" />Задачи клиента
      </h2>
      <div class="tasks-link-actions">
        <Button as-child size="sm">
          <a :href="publicUrl" target="_blank" rel="noopener noreferrer"
            >Открыть задачи<ArrowUpRight class="ml-1.5 size-4" aria-hidden="true"
          /></a>
        </Button>
        <Button type="button" variant="outline" size="sm" @click="copyLink"
          ><Copy class="mr-1.5 size-3.5" aria-hidden="true" />Копировать ссылку</Button
        >
      </div>
    </div>
    <div
      v-if="copied"
      role="status"
      class="copy-toast fixed bottom-5 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm shadow-lg"
    >
      <Check class="size-4 shrink-0 text-emerald-600" aria-hidden="true" />Ссылка на задачи клиента
      скопирована
    </div>
    <Dialog v-model:open="manualCopy">
      <DialogContent class="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Ссылка на задачи клиента</DialogTitle>
          <DialogDescription
            >Автоматическое копирование недоступно. Скопируйте ссылку из поля.</DialogDescription
          >
        </DialogHeader>
        <label class="flex min-w-0 flex-col gap-2 text-sm">
          <span class="font-medium">Ссылка для клиента</span>
          <Input
            :model-value="publicUrl"
            aria-label="Ссылка для клиента"
            readonly
            autofocus
            @focus="selectLink"
          />
        </label>
        <DialogFooter
          ><DialogClose as-child
            ><Button type="button" variant="outline">Закрыть</Button></DialogClose
          ></DialogFooter
        >
      </DialogContent>
    </Dialog>
  </section>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { ArrowUpRight, Check, Copy, ListTodo } from '@lucide/vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const props = defineProps<{ tasks: CustomerTasksLink }>();
const publicUrl = computed(() =>
  props.tasks.url ? new URL(props.tasks.url, window.location.origin).href : ''
);
const copied = ref(false);
const manualCopy = ref(false);
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

async function copyLink() {
  if (!publicUrl.value) return;
  try {
    await navigator.clipboard.writeText(publicUrl.value);
    copied.value = true;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      copied.value = false;
    }, 3000);
  } catch {
    manualCopy.value = true;
  }
}
function selectLink(event: FocusEvent) {
  (event.target as HTMLInputElement).select();
}
watch(publicUrl, () => {
  clearTimeout(copiedTimer);
  copied.value = false;
  manualCopy.value = false;
});
onUnmounted(() => clearTimeout(copiedTimer));
</script>

<style scoped>
.tasks-link-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 20px;
  padding: 16px;
}
.tasks-link-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.copy-toast {
  width: max-content;
  max-width: calc(100vw - 24px);
}
@media (max-width: 600px) {
  .tasks-link-actions {
    width: 100%;
  }
  .tasks-link-actions > * {
    flex: 1 1 auto;
    padding-inline: 10px;
  }
}
</style>
